import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";
import LocationCard from "./FormComponents/LocationCard"

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';

// import { getTechnicians } from "../API/technicianAPI";
// import SignatureCanvas from "react-signature-canvas";

// Import S3 configurations and bucket names
// import {
//   s3ReportsPDFUploader,
//   REPORTS_BUCKET_NAME,
// } from "../Promotions/config/aws-config-joborder-reports";
// import {
//   s3InvoicePDFUploader,
//   INVOICE_BUCKET_NAME,
// } from "../Promotions/config/aws-config-joborder-receipts";
// import {
//   s3TempBucketUploader,
//   TEMP_BUCKET_NAME,
// } from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
// import generateReceipts from "./GenerateReceipts";
// import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditMakatiGpcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  // Function to recursively convert true/false to "True"/"False" in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False";
    }
    return obj;
  };

  // Form change handler
  // const handleInputChange = (event) => {
  //   const { name, value, type, checked } = event.target;
  //   if (type === 'checkbox') {
  //     setJobOrder(prev => ({
  //       ...prev,
  //       [name]: checked
  //     }));
  //   } else {
  //     setJobOrder(prev => {
  //       const newState = {
  //         ...prev,
  //         [name]: value
  //       };

  //       // If changing the start date, ensure end date is not before start date
  //       if (name === 'job_date' && prev.end_date && value && value > prev.end_date) {
  //         newState.end_date = value; // Set end date to start date if it's earlier
  //       }

  //       // If changing the end date, ensure it's not before start date
  //       if (name === 'end_date' && prev.job_date && value && value < prev.job_date) {
  //         return prev; // Don't update if end date is before start date
  //       }

  //       return newState;
  //     });
  //   }
  // };
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (type === 'checkbox') {
      setJobOrder(prev => ({ ...prev, [name]: checked }));
    } else {
      setJobOrder(prev => ({ ...prev, [name]: value }));
    }
  };


  // Handle nested object changes
  const handleNestedChange = (parentKey, childKey, value) => {
    setJobOrder(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  // Handle array item changes
  const handleArrayItemChange = (arrayKey, index, field, value) => {
    setJobOrder(prev => ({
      ...prev,
      [arrayKey]: prev[arrayKey]?.map((item, idx) => 
        idx === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };

  // Save job order function
  const handleSave = async () => {
    try {
      setIsLoading(true);

      // 1) Rebuild inspection report exactly like create form
      const inspection_report = buildInspectionReport();

      // 2) Compose the parameters to reflect current UI state
      const updatedJobOrderParameters = {
        ...jobOrder, // keep existing fields (services, chemical_used, equipment_used, etc.)
        inspection_report,
        service_report_payment_status,
        service_report_payment_method,
      };

      // 3) Prepare the mapper payload (convert booleans to "True"/"False")
      const payload = convertBooleans({
        jobOrder,                        // base job order
        updatedJobOrderParameters,       // the edited parameters you want to render to PDFs
        form: updatedJobOrderParameters,                  // reuse jobOrder here to match the shape from MakatiGpcForm
      });

      // 4) Regenerate PDFs (Report, Certificate, Receipt)
      await submitDocumentFieldMapper(payload);

      // 5) Compute the new S3 links (same convention as create form)
      const newLinks = {
        reports_url:     `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
        certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
        receipts_url:    `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
      };

      // 6) Persist links to backend & update local state so the UI shows the latest
      await patchJobOrderLink(jobOrder.id, newLinks);
      await putJobOrder(jobOrder.id, { 
        ...updatedJobOrderParameters, 
        ...newLinks 
      });
      setJobOrder((prev) => ({ ...prev, ...newLinks }));

      setSuccessMessage("Service report updated and documents regenerated successfully!");
      await handleSendEmail();

    } catch (err) {
      setErrorMessage("Error saving job order: " + (err.message || err));
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage("Error fetching job order: " + (err.message || err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]); // Dependency array ensures the effect runs when ID changes

  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  // ---------------Inspection Report
  const PEST_OPTIONS = useMemo(() => ["Roaches", "Mosq", "Rodents", "Flies", "Ants", "Others"], []);

  const FINDING_OPTIONS = useMemo(() => [
    { value: "", label: "None" },
    { value: "Presence of foul odor", label: "Presence of foul odor" },
    { value: "Trash bins left uncovered", label: "Trash bins left uncovered" },
    { value: "Dirt/Organic Matter Present", label: "Dirt/Organic Matter Present" },
    { value: "Cracks and Crevices", label: "Cracks and Crevices" },
    { value: "Pest Entry Point / Open Windows", label: "Pest Entry Point / Open Windows" },
    { value: "Stagnant Water / Leaking Pipes", label: "Stagnant Water / Leaking Pipes" },
    { value: "Build-up of Old Stocks", label: "Build-up of Old Stocks" },
    { value: "Closed Room or Area Inaccessible", label: "Closed Room or Area Inaccessible" },
    { value: "Active termites found", label: "Active termites found" },
  ], []);

  const RECOMMENDATION_OPTIONS = useMemo(() => [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
  ], []);

  // Normalize pests into a fixed object shape with numeric values
  const normalizePests = useCallback((p) => {
    // base with all six keys = 0
    const base = PEST_OPTIONS.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});

    if (Array.isArray(p)) {
      // OLD shape: [{ type, count }]
      for (const { type, count } of p) {
        if (type && Object.prototype.hasOwnProperty.call(base, type)) {
          base[type] = Number(count) || 0;
        }
      }
    } else if (p && typeof p === "object") {
      // NEW shape: { Roaches: 0, Mosq: 0, ... }
      for (const [k, v] of Object.entries(p)) {
        if (Object.prototype.hasOwnProperty.call(base, k)) {
          base[k] = Number(v) || 0;
        }
      }
    }
    return base;
  }, [PEST_OPTIONS]);

  // Return { select: valueForSelect, other: valueForOtherTextField }
  const coerceOtherSelect = useCallback((raw, OPTIONS) => {
    const v = (raw ?? "").trim();
    if (!v) return { select: "", other: "" };

    // options may be strings or { value, label }
    const values = OPTIONS.map(o => (typeof o === "string" ? o : o.value));
    const labels = OPTIONS.map(o => (typeof o === "string" ? o : o.label));

    // case-insensitive match for robustness
    const matches = (list) =>
      list.find(opt => opt?.localeCompare(v, undefined, { sensitivity: "accent" }) === 0);

    if (matches(values) || matches(labels)) {
      // It's a known option; use it directly as the select value
      return { select: matches(values) || matches(labels), other: "" };
    }

    // Not a known option → select "Other", put original in *_other
    return { select: "Other", other: v };
  }, []);

  const { control, register } = useForm({
   defaultValues: {
     locations: [
       {
         location: "",
         pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
         findings: "",
         findings_other: "",
         recommendation: "",
         recommendation_other: "",
       },
     ],
   },
  });

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
    replace: replaceLocations,
  } = useFieldArray({ control, name: "locations" });

  // watch the entire locations array
  const allLocations = useWatch({ control, name: "locations" }) || [];

  // initialize totals
  const overallTotals = PEST_OPTIONS.reduce((acc, pest) => {
    acc[pest] = 0;
    return acc;
  }, {});

  // sum each location’s pests (supports array OR object)
  allLocations.forEach((loc) => {
    const np = normalizePests(loc?.pests);
    PEST_OPTIONS.forEach((k) => {
      overallTotals[k] += np[k];
    });
  });

  // grand total
  const grandTotal = Object.values(overallTotals).reduce((a, b) => a + b, 0);

  // Consider an inspection row "blank" if location/findings/recommendation are empty AND all pest counts are zero.
  const isBlankInspectionRow = useCallback((row) => {
    const loc = (row?.location || "").trim();
    const findings = (row?.findings || "").trim();
    const recommendation = (row?.recommendation || "").trim();
    const pests = normalizePests(row?.pests);
    const allZero = Object.values(pests).every((n) => Number(n) === 0);
    return !loc && !findings && !recommendation && allZero;
  }, [normalizePests]);

  useEffect(() => {
    const ir = jobOrder?.inspection_report;
    if (!Array.isArray(ir)) return;

    // Map incoming IR -> RHF locations shape
    const mapped = ir
    .slice(0, 15)
    .filter((row) => !isBlankInspectionRow(row)) // ⬅️ filter out empty rows
    .map((row) => {
      const pests = normalizePests(row?.pests); // uses your existing helper

     // Coerce findings/recommendation to "Other" if not in options
     const f = coerceOtherSelect(row?.findings, FINDING_OPTIONS);
     const r = coerceOtherSelect(row?.recommendation, RECOMMENDATION_OPTIONS);

      return {
        location: row?.location || "",
        pests,                                 // { Roaches, Mosq, Rodents, Flies, Ants, Others } as numbers
        findings: f.select,
        findings_other: f.other,
        recommendation: r.select,
        recommendation_other: r.other,
      };
    });

    // If the API sometimes returns an empty array, skip replacing to keep defaults
    if (mapped.length) replaceLocations(mapped);
  }, [jobOrder?.inspection_report, replaceLocations, isBlankInspectionRow, normalizePests, coerceOtherSelect, FINDING_OPTIONS, RECOMMENDATION_OPTIONS]);

  // Build the inspection_report array (exactly 15 rows, like create form)
  const buildInspectionReport = () => {
    return Array.from({ length: 15 }).map((_, idx) => {
      const loc = (allLocations && allLocations[idx]) || {
        location: "",
        pests: {},
        findings: "",
        findings_other: "",
        recommendation: "",
        recommendation_other: "",
      };

      // Prefer "*_other" value when present
      const findings = (loc.findings_other?.trim() || loc.findings || "").trim();
      const recommendation = (loc.recommendation_other?.trim() || loc.recommendation || "").trim();

      // Ensure pest object has all keys and numbers
      const pests = normalizePests(loc.pests);

      // String total, like the API expects
      const total = PEST_OPTIONS.reduce((sum, k) => sum + pests[k], 0);

      return {
        location: loc.location || "",
        pests,
        total: String(total),
        findings,
        recommendation,
      };
    });
  };


  // --- Payment defaults (keep the order stable)
  const DEFAULT_PAYMENT_STATUS = useMemo(() => [
    { label: "Paid",          selection: false },
    { label: "COD",           selection: false },
    { label: "For Collection",selection: false },
  ], []);

  const DEFAULT_PAYMENT_METHOD = useMemo(() => [
    { label: "Cash",  selection: false },
    { label: "Check", selection: false },
    { label: "Free",  selection: false },
  ], []);

  // Local UI state (what the table renders)
  const [service_report_payment_status, setServiceReportPaymentStatus] = useState(DEFAULT_PAYMENT_STATUS);
  const [service_report_payment_method, setServiceReportPaymentMethod]   = useState(DEFAULT_PAYMENT_METHOD);

  // Normalize incoming array/string → align with our defaults by label
  const normalizeByLabel = useCallback((incoming, defaults) => {
    if (Array.isArray(incoming)) {
      const map = new Map(incoming.map(i => [String(i.label || i.value).toLowerCase(), !!i.selection]));
      return defaults.map(d => ({ ...d, selection: !!map.get(d.label.toLowerCase()) }));
    }
    if (typeof incoming === "string") {
      const v = incoming.toLowerCase();
      return defaults.map(d => ({ ...d, selection: d.label.toLowerCase() === v }));
    }
    return defaults;
  }, []);

  useEffect(() => {
    // Pre-fill the radio-like checkboxes from the payload if present
    const status = normalizeByLabel(jobOrder?.service_report_payment_status, DEFAULT_PAYMENT_STATUS);
    const method = normalizeByLabel(jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_METHOD);

    setServiceReportPaymentStatus(status);
    setServiceReportPaymentMethod(method);

    // Keep the outgoing payload in sync as well
    setJobOrder(prev => ({
      ...prev,
      service_report_payment_status: status,
      service_report_payment_method: method,
    }));
  }, [jobOrder?.service_report_payment_status, jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_STATUS, DEFAULT_PAYMENT_METHOD, normalizeByLabel]);

  const handlePaymentStatusChange = (statusIndex) => {
    const next = service_report_payment_status.map((s, i) => ({
      ...s,
      selection: i === statusIndex ? !s.selection : false,
    }));
    setServiceReportPaymentStatus(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_status: next }));
  };

  const handlePaymentMethodChange = (methodIndex) => {
    const next = service_report_payment_method.map((m, i) => ({
      ...m,
      selection: i === methodIndex ? !m.selection : false,
    }));
    setServiceReportPaymentMethod(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_method: next }));
  };

  // ------ Send Email
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);
      if (isNaN(date.getTime())) throw new Error("Invalid date format");
      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch {
      return "Invalid Date";
    }
  }
  const formattedDate = formatDate(jobOrder?.job_date);

  const handleSendEmail = async () => {
    // Build S3 links (same convention used in MakatiGpcForm)
    const generateAttachmentLinks = () => {
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        reports_url:    `${baseUrl}/${jobOrder.id}_report.pdf`,
        certificate_url:`${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url:   `${baseUrl}/${jobOrder.id}_final_receipt.pdf`,
      };
    };

    const attachmentLinks = generateAttachmentLinks();
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key.replace(/_url$/, "").replace(/_/g, " ").toUpperCase();
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .email-container { width: 100%; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; }
          .header, .footer { width: 100%; text-align: center; margin: 0; padding: 20px 0; }
          .content { margin: 20px 0; }
          .content img { max-width: 100%; height: auto; display: block; margin: 0 auto; }
        </style>
      </head>
      <body>
        <div class="email-container">
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>
          <div class="content">
            <p>Sir/Ma'am,</p>
            <p>Good day!</p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on ${formattedDate}.</p>
            <p>We thank you and please let us know if you have any questions.</p>
            <p>Sincerely,<br/>Boaz Pest Control - Accounts Team</p>
            <ul>${attachmentLinksHTML}</ul>
            <hr />
          </div>
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
            <div class="footer">
              <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
              <hr />
              <footer>
                <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
              </footer>
            </div>
          </a>
        </div>
      </body>
    </html>`;

    const lambdaPayload = {
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      await sendEmailQueue(lambdaPayload);
      setSuccessMessage("Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage("Failed to send email.");
    }
  };



  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={2}>
                  <TextField
                    name="id"
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={2}>
                  <FormControl fullWidth>
                    <InputLabel id="job-order-status-label">
                      Job Order Status
                    </InputLabel>
                    <Select
                      labelId="job-order-status-label"
                      name="status"
                      value={jobOrder?.status || ""}
                      label="Job Order Status"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Not Done">Not Done</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Cancelled By Client">
                        Cancelled By Client
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client ID"
                    label="Client ID"
                    value={jobOrder?.client_id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client Name"
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={10}>
                      <TextField
                        label="Job Site Address"
                        variant="outlined"
                        fullWidth
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder.client.address.house_unit_street || ""
                            }, ${jobOrder.client.address.barangay || ""
                            }, ${jobOrder.client.address.city || ""
                            }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                            : ""
                        }
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                      <TextField
                        name="area"
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact
                        ? `${jobOrder.client.contact.lastname || ""}, ${jobOrder.client.contact.firstname || ""
                        }`
                        : ""
                    } // Display the contact's full name or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    variant="outlined"
                    value={jobOrder.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact?.number
                        ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                        : ""
                    } // Display the contact number or an empty string if not available
                    disabled
                  />
                </Grid>                
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    label="Location Type"
                    value={
                      client?.location_type
                        ? client.location_type
                        : ""
                    }
                    fullWidth
                    variant="outlined"
                    sx={{ fontSize: "0.875rem" }}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{                    
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={jobOrder.special_notes || ""}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Appointment
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="appointment-type-label">
                      Appointment Type
                    </InputLabel>
                    <Select
                      labelId="appointment-type-label"
                      name="appointment_type"
                      value={jobOrder?.appointment_type || ""}
                      label="appointment_type"
                      disabled
                    >
                      <MenuItem value="Initial">Initial</MenuItem>
                      <MenuItem value="Follow-up">Follow-up</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="treatment-type-label">
                      Treatment Type
                    </InputLabel>
                    <Select
                      labelId="treatment-type-label"
                      name="treatment_type"
                      value={jobOrder?.treatment_type || ""}
                      label="treatment_type"
                      disabled
                    >
                      <MenuItem value="QB">QB</MenuItem>
                      <MenuItem value="FFUP">FFUP</MenuItem>
                      <MenuItem value="LT">LT</MenuItem>
                      <MenuItem value="QBLT">QBLT</MenuItem>
                      <MenuItem value="Backjob">Backjob</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Inspection & Pest Count (Mobile) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Inspection Report
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Stack spacing={2}>
                {locationFields.map((loc, idx) => (
                  <LocationCard
                    key={loc.id}
                    control={control}
                    register={register}
                    index={idx}
                    removeLocation={removeLocation}
                  />
                ))}

                <Button
                  variant="contained"
                  disabled={locationFields.length >= 15}
                  fullWidth
                  onClick={() =>
                    appendLocation({
                      location: "",
                      // pests: [{ type: "", count: "" }],
                      pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
                      findings: "",
                      findings_other: "",
                      recommendation: "",
                      recommendation_other: "",
                    })
                  }
                >
                  Add New Location ({locationFields.length}/15)
                </Button>

                {/* Overall Total Pests Section */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                        >
                          OVERALL TOTAL PESTS
                        </Typography>
                      }
                      sx={{ pb: 0, px: 2, pt: 2 }}
                    />
                    <CardContent sx={{ px: 2, pb: 2 }}>
                      {PEST_OPTIONS.map((pest) => (
                        <Typography
                          key={pest}
                          variant="body2"
                          sx={{ display: "flex", justifyContent: "space-between" }}
                        >
                          {pest}
                          <span>{overallTotals[pest] || 0}</span>
                        </Typography>
                      ))}

                      <Typography
                        variant="subtitle1"
                        sx={{ mt: 1, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}
                      >
                        Total
                        <span>{grandTotal}</span>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Activities Done */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Activities Done</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <FormGroup>
                {(jobOrder.services || []).map((service, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={service.activityChecked || false}
                        onChange={(e) => {
                          const newServices = [...(jobOrder.services || [])];
                          newServices[idx] = {
                            ...newServices[idx],
                            activityChecked: e.target.checked
                          };
                          setJobOrder(prev => ({
                            ...prev,
                            services: newServices
                          }));
                        }}
                      />
                    }
                    label={service.activity?.replace(/_/g, ' ') || ''}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Services Performed */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Services Performed</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <FormGroup>
                {(jobOrder.services || []).map((service, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={service.serviceChecked || false}
                        onChange={(e) => {
                          const newServices = [...(jobOrder.services || [])];
                          newServices[idx] = {
                            ...newServices[idx],
                            serviceChecked: e.target.checked
                          };
                          setJobOrder(prev => ({
                            ...prev,
                            services: newServices
                          }));
                        }}
                      />
                    }
                    label={service.service?.replace(/_/g, ' ') || ''}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Notes Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Reminder Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Reminder Notes"
                name="reminder_notes"
                value={jobOrder.reminder_notes || ""}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={6}
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Chemical Used Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Chemical Used / Batch # / Expiration
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: "bold" }}
                        >
                          Dilution
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment Used
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(jobOrder.chemical_used || []).map((chem, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{chem.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.chemical || ""}
                              onChange={(e) => handleArrayItemChange('chemical_used', idx, 'chemical', e.target.value)}
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.dilution || ""}
                              onChange={(e) => handleArrayItemChange('chemical_used', idx, 'dilution', e.target.value)}
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          {jobOrder.equipment_used?.length > idx ? (
                            <>
                              <TableCell>
                                {jobOrder.equipment_used[idx].label}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={jobOrder.equipment_used[idx].usage}
                                  onChange={(e) => handleArrayItemChange('equipment_used', idx, 'usage', e.target.checked)}
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell />
                              <TableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Against Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  CONTROL AGAINST
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.crawling_insects || false}
                      onChange={(e) => handleNestedChange('control_against', 'crawling_insects', e.target.checked)}
                    />
                  }
                  label="Crawling Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.flying_insects || false}
                      onChange={(e) => handleNestedChange('control_against', 'flying_insects', e.target.checked)}
                    />
                  }
                  label="Flying Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.rodent_control || false}
                      onChange={(e) => handleNestedChange('control_against', 'rodent_control', e.target.checked)}
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.others || false}
                      onChange={(e) => handleNestedChange('control_against', 'others', e.target.checked)}
                    />
                  }
                  label="Others"
                />
              </FormGroup>

              {jobOrder.control_against?.others && (
                <TextField
                  label="Please specify"
                  value={jobOrder.control_against?.others_text || ""}
                  onChange={(e) => handleNestedChange('control_against', 'others_text', e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Service Fee Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Service Fee
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Service Fee"
                name="service_fee"
                value={jobOrder.service_fee || ""}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₱</InputAdornment>
                  )
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Status Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={<Typography variant="h6">Payment Status</Typography>}
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableBody>
                    {(service_report_payment_status || []).map((statusOpt, idx) => {
                      const methodOpt = (service_report_payment_method || [])[idx] || {};
                      return (
                        <TableRow key={statusOpt.label} hover>
                          <TableCell>{statusOpt.label}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={statusOpt.selection}
                              onChange={() => handlePaymentStatusChange(idx)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'text.secondary', p: 0 }}>
                            •
                          </TableCell>
                          <TableCell/>
                          <TableCell>{methodOpt.label || ""}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={methodOpt.selection}
                              onChange={() => handlePaymentMethodChange(idx)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>  
                
        {/* Service Accomplished by Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Service Accomplished by:
              </Typography>
              <Grid container spacing={2}>
                { (jobOrder.accomplished_by_technician || jobOrder.technicians || [])
                    .map((name, i) =>
                      typeof name === "string"
                        ? name
                        : `${name.firstname} ${name.lastname}`
                    )
                    .map((techName, i) => (
                  <Grid key={i} item xs={12}>
                    <TextField
                      label={`Technician ${i + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={techName}
                      disabled
                    />
                  </Grid>
                ))}

                {/* Accomplished Signature */}
                <Grid item xs={12}>
                  {jobOrder.accomplishedSignature && (
                    <Box
                      component="img"
                      src={jobOrder.accomplishedSignature}
                      alt="Accomplished Signature"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        objectFit: "contain",
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                      }}
                    />
                  )}
                </Grid>

                {/* Technician Signatory dropdown, right below the signature */}
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="technician-signatory-label">
                      Technician Signatory
                    </InputLabel>
                    <Select
                      labelId="technician-signatory-label"
                      name="technician_signatory"
                      value={jobOrder.technician_signatory || ""}
                      label="Technician Signatory"
                      disabled
                    >
                      {jobOrder.technicians?.map((tech) => (
                        <MenuItem
                          key={tech.id}
                          value={`${tech.firstname} ${tech.lastname}`}
                        >
                          {`${tech.firstname} ${tech.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder?.technician_signature_uploaded_at && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        🔷 Technician Signature Uploaded At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                        {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrder?.job_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrder?.start_time || ""}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrder?.end_date || ""}
                    onChange={handleInputChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        min: jobOrder?.job_date || undefined
                      }
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrder?.end_time || ""}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      
        {/* Acknowledged Signature Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For Client Only
                </Typography>
              }
              sx={{ px: 2, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Your Satisfaction Rating:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                (1 – lowest, 5 – highest)
              </Typography>
              <Rating
                name="satisfaction_rating"
                value={Number(jobOrder.satisfaction_rating) || 0}
                readOnly
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Acknowledged By:
              </Typography>
              {jobOrder.acknowledgedSignature && (
                <Box
                  component="img"
                  src={jobOrder.acknowledgedSignature}
                  alt="Acknowledged Signature"
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    objectFit: "contain",
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                  }}
                />
              )}
              <TextField
                label="Client Representative"
                name="acknowledgedClientSignatory"
                variant="outlined"
                fullWidth
                size="small"
                value={jobOrder.acknowledgedClientSignatory || ""}
                disabled
              />

              {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
              {!isTechnician && jobOrder?.client_signature_uploaded_at && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🟢 Client Signature Uploaded At:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {jobOrder?.receipts_url ||
                  jobOrder?.certificate_url ||
                  jobOrder?.reports_url ? (
                  <>
                    {jobOrder?.receipts_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${jobOrder.receipts_url}?v=${encodeURIComponent(jobOrder.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {jobOrder?.certificate_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${jobOrder.certificate_url}?v=${encodeURIComponent(jobOrder.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Certificate.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {jobOrder?.reports_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${jobOrder.reports_url}?v=${encodeURIComponent(jobOrder.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Reports.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder?.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Save and Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              fontSize: "0.875rem",
            }}
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default EditMakatiGpcForm;
